import React, { createContext, useContext, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { X, ArrowRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LeadCtx = createContext(null);
export const useLead = () => useContext(LeadCtx);

const empty = { name: "", email: "", business: "", phone: "", website: "", goals: "", message: "" };

const Field = ({ label, name, value, onChange, type = "text", area, testid, required }) => (
  <label className="block">
    <span className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-zinc-500">{label}{required && <span className="text-teal"> *</span>}</span>
    {area ? (
      <textarea
        data-testid={testid} name={name} value={value} onChange={onChange} rows={3} required={required}
        className="w-full resize-none border-b border-zinc-300 bg-transparent py-2 font-sans text-ink outline-none transition-colors placeholder:text-zinc-400 focus:border-teal"
      />
    ) : (
      <input
        data-testid={testid} name={name} value={value} onChange={onChange} type={type} required={required}
        className="w-full border-b border-zinc-300 bg-transparent py-2 font-sans text-ink outline-none transition-colors placeholder:text-zinc-400 focus:border-teal"
      />
    )}
  </label>
);

export const LeadProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("consultation");
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const openLead = useCallback((t = "consultation") => {
    setType(t);
    setForm(empty);
    setOpen(true);
  }, []);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { type, ...form });
      toast.success(type === "audit" ? "Your audit request is in — we'll be in touch within 1 business day." : "Thanks! We'll reach out to schedule your consultation shortly.");
      setOpen(false);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isAudit = type === "audit";

  return (
    <LeadCtx.Provider value={{ openLead }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg gap-0 rounded-none border-0 p-0" data-testid="lead-dialog">
          <div className="bg-ink p-6 md:p-8">
            <DialogHeader>
              <span className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-teal">
                {isAudit ? "Marketing Growth Audit" : "Book a Consultation"}
              </span>
              <DialogTitle className="font-display text-3xl font-semibold leading-none tracking-tighter text-white">
                {isAudit ? "Let's find your gaps." : "Let's build your presence."}
              </DialogTitle>
            </DialogHeader>
            <p className="mt-3 font-sans text-sm text-zinc-400">
              {isAudit ? "Tell us about your business and we'll audit your online presence, content and lead flow." : "Share a few details and we'll reach out to schedule a strategy call."}
            </p>
          </div>
          <form onSubmit={submit} className="grid gap-5 bg-white p-6 md:p-8">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Name" name="name" value={form.name} onChange={onChange} testid="lead-name-input" required />
              <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} testid="lead-email-input" required />
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Business" name="business" value={form.business} onChange={onChange} testid="lead-business-input" />
              <Field label={isAudit ? "Website URL" : "Phone"} name={isAudit ? "website" : "phone"} value={isAudit ? form.website : form.phone} onChange={onChange} testid="lead-secondary-input" />
            </div>
            <Field label={isAudit ? "What are you hoping to improve?" : "How can we help?"} name={isAudit ? "goals" : "message"} value={isAudit ? form.goals : form.message} onChange={onChange} area testid="lead-message-input" />
            <button
              type="submit" disabled={loading} data-testid="lead-submit-button"
              className="group mt-1 flex items-center justify-center gap-3 bg-teal py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-ink disabled:opacity-60"
            >
              {loading ? "Sending..." : isAudit ? "Start My Audit" : "Request Consultation"}
              <ArrowRight weight="bold" className="transition-transform group-hover:translate-x-1" />
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </LeadCtx.Provider>
  );
};
